// src/admin/ManageSubscribers.js

import React, { useEffect, useState } from 'react';
import { db } from '../config/firebaseConfig';
import { collection, query, orderBy, getDocs, deleteDoc, doc } from 'firebase/firestore';

const ManageSubscribers = () => {
  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(true);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 15;

  useEffect(() => {
    const fetchSubscribers = async () => {
      try {
        const q = query(collection(db, "bookSubscriptions"), orderBy("createdAt", "desc"));
        const querySnapshot = await getDocs(q);
        const subs = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setSubscribers(subs);
      } catch (error) {
        console.error("Error fetching subscribers:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchSubscribers();
  }, []);

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this subscriber?")) {
      try {
        await deleteDoc(doc(db, "bookSubscriptions", id));
        setSubscribers((prev) => prev.filter((sub) => sub.id !== id));
      } catch (error) {
        console.error("Error deleting subscriber:", error);
        alert("Failed to delete subscriber.");
      }
    }
  };

  // Export to CSV Function
  const exportToCSV = () => {
    const csvRows = [];
    // Header
    csvRows.push(['Email', 'Book Name', 'Date Subscribed'].join(','));
    
    // Data
    subscribers.forEach(sub => {
      const date = sub.createdAt ? sub.createdAt.toDate().toLocaleDateString() : 'N/A';
      csvRows.push([sub.email, sub.bookName, date].join(','));
    });

    const csvData = csvRows.join('\n');
    const blob = new Blob([csvData], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.setAttribute('hidden', '');
    a.setAttribute('href', url);
    a.setAttribute('download', 'little_hibba_subscribers.csv');
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = subscribers.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(subscribers.length / itemsPerPage);

  if (loading) return <div className="p-4">Loading subscribers...</div>;

  return (
    <div className="container-fluid p-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="mb-0">Book Launch Subscribers</h2>
        <button className="btn btn-success" onClick={exportToCSV} disabled={subscribers.length === 0}>
          <i className="bi bi-file-earmark-excel me-2"></i> Export to CSV
        </button>
      </div>

      <div className="card shadow-sm">
        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="table table-hover mb-0 align-middle">
              <thead className="bg-light">
                <tr>
                  <th className="ps-4">Date</th>
                  <th>Email Address</th>
                  <th>Target Book</th>
                  <th className="text-end pe-4">Actions</th>
                </tr>
              </thead>
              <tbody>
                {currentItems.length === 0 ? (
                  <tr><td colSpan="4" className="text-center py-4">No subscribers found yet.</td></tr>
                ) : (
                  currentItems.map((sub) => (
                    <tr key={sub.id}>
                      <td className="ps-4 text-muted small">
                        {sub.createdAt?.toDate().toLocaleDateString()}
                      </td>
                      <td className="fw-medium">{sub.email}</td>
                      <td>
                        <span className="badge bg-warning text-dark">{sub.bookName}</span>
                      </td>
                      <td className="text-end pe-4">
                        <button className="btn btn-outline-danger btn-sm" onClick={() => handleDelete(sub.id)} title="Delete Subscriber">
                          <i className="bi bi-trash"></i>
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pagination */}
        {subscribers.length > itemsPerPage && (
          <div className="card-footer bg-white d-flex justify-content-end py-3">
            <nav>
              <ul className="pagination mb-0">
                <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                  <button className="page-link" onClick={() => setCurrentPage(currentPage - 1)}>Prev</button>
                </li>
                <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                  <button className="page-link" onClick={() => setCurrentPage(currentPage + 1)}>Next</button>
                </li>
              </ul>
            </nav>
          </div>
        )}
      </div>
    </div>
  );
};

export default ManageSubscribers;